// src/data/weekExp.js
// EXP-System: jede erledigte Einheit einer Woche bringt EXP.
// Freischaltung der nächsten Woche: ab UNLOCK_RATIO (90 %) der Wochen-EXP, sobald seit der
// letzten Freischaltung ein neuer Montag war, oder sofort bei 100 % (canUnlockNextWeek).

import { PERSON_SLOTS } from "./languages";

// EXP pro gelöstem Lückentext
export const EXP_PER_UNIT = 10;

// Vokabeln: EXP je Wort nach erreichter Stufe (Box), höchstens 10 pro Wort
export const VOCAB_EXP_BY_BOX = { 3: 2, 4: 5, 5: 10 };
export const VOCAB_EXP_MAX = 10;

// Phrasen/Themen: feste Summe pro Woche, anteilig nach Fortschritt
export const PHRASES_EXP_MAX = 150;

// Freie Übersetzung: EXP pro übersetztem Satz
export const EXP_PER_TRANSLATION = 30;

// Verben werden in Prozent gemessen, 100 % entsprechen diesem EXP-Wert
export const VERB_EXP_BY_PACE = { easy: 200, medium: 300, hard: 400 };

export const UNLOCK_RATIO = 0.9;

// Lückentexte pro Woche je Tempo
export const GAPS_PER_WEEK_BY_PACE = { hard: 20, medium: 15, easy: 10 };

export function getGapsTargetForPace(pace) {
  return GAPS_PER_WEEK_BY_PACE[pace] ?? GAPS_PER_WEEK_BY_PACE.medium;
}

/**
 * Fortschritt im Verbtraining einer Woche in Prozent (0–100):
 * 80 % Tabellen (2 gute Durchläufe oder Einzelmodus = fertig, 1 Durchlauf = halb),
 * 20 % Einzelformen (jede jemals richtige Form 0,5 %).
 */
export function computeVerbsPercent(verbStates) {
  const list = Object.values(verbStates || {});
  if (list.length === 0) return 0;

  let completedEquivalent = 0;
  let singlesEverCorrect = 0;
  list.forEach((s) => {
    const goodRuns = s.tableGoodRuns || 0;
    if (goodRuns >= 2 || (s.mode || "table") === "single") completedEquivalent += 1;
    else if (goodRuns >= 1) completedEquivalent += 0.5;

    const forms = s.forms || {};
    PERSON_SLOTS.forEach((key) => {
      if (forms[key] && forms[key].everCorrect) singlesEverCorrect += 1;
    });
  });

  const tablePart = (completedEquivalent / list.length) * 80;
  const singlePart = Math.min(20, singlesEverCorrect * 0.5);
  return Math.min(100, tablePart + singlePart);
}

function clamp(done, target) {
  return Math.max(0, Math.min(done || 0, target || 0));
}

/** EXP eines Wortes nach seiner Box (0–5) */
export function vocabExpForBox(box) {
  if (!box || box < 3) return 0;
  return VOCAB_EXP_BY_BOX[Math.min(5, box)];
}

/**
 * Rechnet den Fortschritt einer Woche in EXP um.
 * vocabBoxes: Box jedes Wortes im Wochenset
 */
export function computeWeekExp({
  pace,
  vocabBoxes = [],
  vocabTarget,
  verbsPercent,
  gapsDone,
  gapsTarget,
  phrasesDone,
  phrasesTarget,
  translationsDone,
  translationsTarget,
}) {
  const verbMax = VERB_EXP_BY_PACE[pace] ?? VERB_EXP_BY_PACE.medium;
  const vocabMax = (vocabTarget || 0) * VOCAB_EXP_MAX;
  const vocabExp = vocabBoxes.reduce((sum, box) => sum + vocabExpForBox(box), 0);

  const parts = {
    vocab: {
      exp: Math.min(vocabExp, vocabMax),
      max: vocabMax,
    },
    verbs: {
      exp: Math.round((verbMax * clamp(verbsPercent, 100)) / 100),
      max: verbMax,
    },
    gaps: {
      exp: clamp(gapsDone, gapsTarget) * EXP_PER_UNIT,
      max: (gapsTarget || 0) * EXP_PER_UNIT,
    },
    phrases: {
      // Themen-Wochen zählen in Bruchteilen (z. B. 1,333 von 4), daher runden
      exp:
        phrasesTarget > 0
          ? Math.round((clamp(phrasesDone, phrasesTarget) / phrasesTarget) * PHRASES_EXP_MAX)
          : 0,
      max: phrasesTarget > 0 ? PHRASES_EXP_MAX : 0,
    },
    translations: {
      exp: clamp(translationsDone, translationsTarget) * EXP_PER_TRANSLATION,
      max: (translationsTarget || 0) * EXP_PER_TRANSLATION,
    },
  };

  const values = Object.values(parts);
  const total = values.reduce((sum, p) => sum + p.exp, 0);
  const max = values.reduce((sum, p) => sum + p.max, 0);
  const unlockAt = Math.ceil(max * UNLOCK_RATIO);

  return {
    parts,
    total,
    max,
    unlockAt,
    reached: max > 0 && total >= unlockAt,
    complete: max > 0 && total >= max,
  };
}

// Tagesnummer (Tage seit 1970) des Montags der Kalenderwoche, deutsche Zeit
function mondayIndex(date) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type).value);
  const day = Math.floor(Date.UTC(get("year"), get("month") - 1, get("day")) / 86400000);
  // 1.1.1970 war ein Donnerstag: (day + 3) % 7 ergibt 0 = Montag
  return day - ((day + 3) % 7);
}

/** true, wenn `now` in einer späteren Kalenderwoche (ab Montag) liegt als `since` */
export function isLaterCalendarWeek(since, now = new Date()) {
  if (!since) return false;
  const start = new Date(since);
  if (Number.isNaN(start.getTime())) return false;
  return mondayIndex(now) > mondayIndex(start);
}

/**
 * Darf die nächste Woche aufgehen? 100 % sofort, 90 % erst ab dem Montag nach
 * der letzten Freischaltung (unlockedAt).
 */
export function canUnlockNextWeek(exp, unlockedAt, now = new Date()) {
  if (exp.complete) return true;
  return exp.reached && isLaterCalendarWeek(unlockedAt, now);
}
